import { graphql } from 'react-relay';
import { useCallback, useContext, useEffect } from 'react';

import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import { useEditorAnalyticsEvent } from '../useEditorAnalyticsEvent.ts';

import type {
	useSpaceKeyWatcherContentSpaceKeyQuery$data as ContentSpaceKeyQueryType,
	useSpaceKeyWatcherContentSpaceKeyQuery$variables as ContentSpaceKeyQueryVariables,
} from './__generated__/useSpaceKeyWatcherContentSpaceKeyQuery.graphql.ts';

export const useSpaceKeyWatcher = ({ contentType }: { contentType: string }): void => {
	const { match, replace, toUrl } = useContext(RoutesContext);
	const { contentId, spaceKey: spaceKeyFromUrl } = match?.params ?? {};

	const { triggerAnalyticsEvent } = useEditorAnalyticsEvent({
		contentId,
		contentType,
		source: 'editPageScreen',
		subject: 'editor',
		collabService: 'ncs',
	});

	// Replaces the URL to match the given space key
	const changeUrl = useCallback(
		(spaceKey: string) => {
			// Only changing the space key
			const params = {
				...match?.params,
				query: match?.query,
				spaceKey,
			};

			const url = toUrl(match?.name, params);
			if (url !== null) {
				replace(url);
			}
		},
		[replace, toUrl, match],
	);
	// Changes the URL if the new space key from the move event is different from the current
	const onMoveSubscriptionEvent = useCallback(
		(_eventName: string, response: any) => {
			const newSpaceAlias = response?.pageMoved?.newSpaceAlias ?? null;

			if (
				newSpaceAlias !== null &&
				newSpaceAlias?.toLowerCase() !== spaceKeyFromUrl?.toLowerCase()
			) {
				changeUrl(newSpaceAlias);
			}

			// Fire analytics to track when the page or blog was moved
			const eventName = contentType === 'page' ? 'pageMoved' : 'blogMoved';
			void triggerAnalyticsEvent(eventName, {
				editorType: 'next',
				...response,
			});
		},
		[changeUrl, contentType, spaceKeyFromUrl, triggerAnalyticsEvent],
	);

	// Handles the case where the page is moved while the user is editing it
	useSubscriptionEvent({
		contentId,
		contentType,
		subscriberId: 'UseSpaceKeyWatcherSubscription',
		delta: 'pageMoved',
		onDataReceived: onMoveSubscriptionEvent,
	});

	// Handles the case where the user returns to a bookmarked page which has since been moved
	useEffect(() => {
		const fetchSpaceKey = async () => {
			const { data } = await queryInterop<ContentSpaceKeyQueryType, ContentSpaceKeyQueryVariables>({
				query: graphql`
					query useSpaceKeyWatcherContentSpaceKeyQuery($contentId: ID!) {
						content(id: $contentId, status: ["current", "draft"]) {
							nodes {
								id
								space {
									id
									key
									alias
								}
							}
						}
					}
				`,
				variables: {
					contentId,
				},
				fetchPolicy: 'network-only',
			});

			const contentSpaceKeyAsKey = data?.content?.nodes?.[0]?.space?.key ?? null;
			const contentSpaceAliasAsKey = data?.content?.nodes?.[0]?.space?.alias ?? null;
			const contentSpaceKey = contentSpaceAliasAsKey || contentSpaceKeyAsKey;
			if (contentSpaceKey !== null && contentSpaceKey !== spaceKeyFromUrl) {
				changeUrl(contentSpaceKey);
			}
		};

		if (contentId) {
			void fetchSpaceKey();
		}
	}, [contentId, changeUrl, spaceKeyFromUrl]);
};
